import { useState, useEffect, useCallback } from "react";
import {
  Box,
  Card,
  Typography,
  Stack,
  Chip,
  Button,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  IconButton,
  Skeleton,
  Tooltip,
  alpha,
} from "@mui/material";
import CreditCardRoundedIcon from "@mui/icons-material/CreditCardRounded";
import AddCardRoundedIcon from "@mui/icons-material/AddCardRounded";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import VerifiedRoundedIcon from "@mui/icons-material/VerifiedRounded";
import LockRoundedIcon from "@mui/icons-material/LockRounded";
import {
  listSuperpeCards,
  getSuperpeCardAesKey,
  registerSuperpeCard,
  deleteSuperpeCard,
} from "../api";

/* AES-256-GCM card-number encryption in the browser (SubtleCrypto) */
const b64ToBytes = (b64) => Uint8Array.from(atob(b64), (c) => c.charCodeAt(0));
const bytesToB64 = (bytes) =>
  btoa(String.fromCharCode(...new Uint8Array(bytes)));

async function encryptCardNumber(base64Key, pan) {
  const keyBytes = b64ToBytes(base64Key).slice(0, 32); // AES-256 uses first 32 bytes
  const key = await crypto.subtle.importKey(
    "raw",
    keyBytes,
    { name: "AES-GCM" },
    false,
    ["encrypt"],
  );
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const enc = new TextEncoder().encode(pan);
  const ctWithTag = new Uint8Array(
    await crypto.subtle.encrypt({ name: "AES-GCM", iv, tagLength: 128 }, key, enc),
  );
  // browser appends the 16-byte tag to the ciphertext; SuperPe wants them split
  const tag = ctWithTag.slice(ctWithTag.length - 16);
  const ciphertext = ctWithTag.slice(0, ctWithTag.length - 16);
  return { ciphertext: bytesToB64(ciphertext), iv: bytesToB64(iv), tag: bytesToB64(tag) };
}

/* Network inference + branding */
const inferNetwork = (pan) => {
  const first = String(pan || "")[0];
  if (first === "4") return "VISA";
  if (first === "5") return "MASTERCARD";
  if (first === "6") return "RUPAY";
  return "";
};

const NETWORK_META = {
  VISA: { label: "VISA", color: "#1a1f71" },
  MASTERCARD: { label: "Mastercard", color: "#eb001b" },
  RUPAY: { label: "RuPay", color: "#097b3e" },
  "": { label: "Card", color: "#6c5ce7" },
};

const formatCardNumber = (v) =>
  v
    .replace(/\D/g, "")
    .slice(0, 16)
    .replace(/(.{4})/g, "$1 ")
    .trim();

function SavedCard({ card, onDelete }) {
  const meta = NETWORK_META[card.network] || NETWORK_META[""];
  const usable = !!card.usable;
  return (
    <Card
      sx={{
        p: 2.5,
        borderRadius: 3,
        color: "#fff",
        position: "relative",
        overflow: "hidden",
        width: "100%",
        maxWidth: 360,
        minHeight: 168,
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
        background: `linear-gradient(135deg, ${meta.color} 0%, ${alpha(
          meta.color,
          0.75,
        )} 100%)`,
        boxShadow: `0 10px 24px ${alpha(meta.color, 0.35)}`,
      }}
    >
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
        <CreditCardRoundedIcon sx={{ fontSize: 30, opacity: 0.9 }} />
        <Stack direction="row" spacing={1} alignItems="center">
          <Chip
            size="small"
            icon={
              usable ? (
                <VerifiedRoundedIcon sx={{ fontSize: 15, color: "#fff !important" }} />
              ) : undefined
            }
            label={usable ? "Verified" : "Pending verification"}
            sx={{
              fontWeight: 700,
              fontSize: 10.5,
              color: "#fff",
              bgcolor: usable
                ? alpha("#2e7d32", 0.9)
                : alpha("#ed6c02", 0.95),
            }}
          />
          <Tooltip title="Delete card">
            <IconButton
              size="small"
              onClick={() => onDelete(card)}
              sx={{ color: "#fff", "&:hover": { bgcolor: "rgba(255,255,255,0.18)" } }}
            >
              <DeleteOutlineRoundedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>
      </Stack>

      <Typography
        sx={{ fontSize: 20, fontWeight: 700, letterSpacing: 2, mt: 1 }}
      >
        •••• •••• •••• {card.last4}
      </Typography>

      <Stack direction="row" justifyContent="space-between" alignItems="flex-end">
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="caption" sx={{ opacity: 0.75, display: "block" }}>
            Card holder
          </Typography>
          <Typography fontWeight={600} fontSize={14} noWrap>
            {card.nameOnCard || "—"}
          </Typography>
        </Box>
        <Box sx={{ textAlign: "right" }}>
          <Typography variant="caption" sx={{ opacity: 0.75, display: "block" }}>
            Expires
          </Typography>
          <Typography fontWeight={600} fontSize={14}>
            {String(card.expiryMonth || "").padStart(2, "0")}/
            {String(card.expiryYear || "").slice(-2)}
          </Typography>
        </Box>
      </Stack>

      <Typography
        sx={{
          position: "absolute",
          right: 14,
          bottom: 10,
          fontWeight: 800,
          fontSize: 13,
          opacity: 0.9,
          textTransform: "uppercase",
        }}
      >
        {meta.label}
      </Typography>
    </Card>
  );
}

export default function Cards() {
  const [cards, setCards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [feedback, setFeedback] = useState(null);

  const [open, setOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    number: "",
    name: "",
    month: "",
    year: "",
  });
  const [formError, setFormError] = useState(null);

  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const fetchCards = useCallback(() => {
    setLoading(true);
    listSuperpeCards()
      .then(({ data }) => setCards(data.cards || []))
      .catch(() => setCards([]))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    fetchCards();
  }, [fetchCards]);

  const rawNumber = form.number.replace(/\D/g, "");
  const network = inferNetwork(rawNumber);
  const numberOk = rawNumber.length === 16;
  const nameOk = form.name.trim().length >= 2;
  const monthNum = Number(form.month);
  const monthOk = monthNum >= 1 && monthNum <= 12;
  const yearOk = /^\d{2}$/.test(form.year);
  const formOk = numberOk && nameOk && monthOk && yearOk;

  const resetForm = () => {
    setForm({ number: "", name: "", month: "", year: "" });
    setFormError(null);
  };

  const handleAdd = async () => {
    if (!formOk) return;
    setSubmitting(true);
    setFormError(null);
    try {
      const { data: keyData } = await getSuperpeCardAesKey();
      const encryptedData = await encryptCardNumber(keyData.base64Key, rawNumber);
      const { data } = await registerSuperpeCard({
        last4: rawNumber.slice(-4),
        cardFirst6: rawNumber.slice(0, 6),
        expiryMonth: String(monthNum).padStart(2, "0"),
        expiryYear: form.year,
        nameOnCard: form.name.trim(),
        network,
        encryptedData,
      });
      const card = data.card || data;
      setFeedback({
        severity: "success",
        msg: card && card.usable
          ? "Card added and verified"
          : "Card added — verification pending; you can use it once verified.",
      });
      setOpen(false);
      resetForm();
      fetchCards();
    } catch (e) {
      setFormError(e.response?.data?.error || "Failed to add card");
    }
    setSubmitting(false);
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await deleteSuperpeCard(deleteTarget.id);
      setFeedback({ severity: "success", msg: "Card removed." });
      setDeleteTarget(null);
      fetchCards();
    } catch (e) {
      setFeedback({
        severity: "error",
        msg: e.response?.data?.error || "Failed to delete card",
      });
      setDeleteTarget(null);
    }
    setDeleting(false);
  };

  return (
    <Box>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        mb={3}
        flexWrap="wrap"
        gap={2}
      >
        <Box>
          <Typography variant="h5" fontWeight={700}>
            My Cards
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Save a card once, then top up your wallet via PhonePe checkout
          </Typography>
        </Box>
        <Button
          variant="contained"
          size="large"
          startIcon={<AddCardRoundedIcon />}
          onClick={() => {
            resetForm();
            setOpen(true);
          }}
          sx={{
            borderRadius: 3,
            px: 3,
            textTransform: "none",
            fontWeight: 700,
            boxShadow: (t) => `0 8px 20px ${alpha(t.palette.primary.main, 0.3)}`,
          }}
        >
          Add Card
        </Button>
      </Stack>

      {feedback && (
        <Alert
          severity={feedback.severity}
          sx={{ mb: 2, borderRadius: 2 }}
          onClose={() => setFeedback(null)}
        >
          {feedback.msg}
        </Alert>
      )}

      {loading ? (
        <Grid container spacing={2}>
          {[0, 1, 2].map((i) => (
            <Grid item xs={12} sm={6} md={4} key={i}>
              <Skeleton variant="rounded" height={168} sx={{ borderRadius: 3 }} />
            </Grid>
          ))}
        </Grid>
      ) : cards.length === 0 ? (
        <Card sx={{ p: 6, textAlign: "center", borderRadius: 3 }}>
          <CreditCardRoundedIcon
            sx={{ fontSize: 52, color: "text.disabled", mb: 1 }}
          />
          <Typography fontWeight={600} mb={0.5}>
            No saved cards yet
          </Typography>
          <Typography variant="body2" color="text.secondary" mb={2}>
            Add a card to top up your wallet with PhonePe checkout
          </Typography>
          <Button
            variant="outlined"
            startIcon={<AddCardRoundedIcon />}
            onClick={() => {
              resetForm();
              setOpen(true);
            }}
            sx={{ borderRadius: 3, textTransform: "none" }}
          >
            Add your first card
          </Button>
        </Card>
      ) : (
        <Grid container spacing={2}>
          {cards.map((c) => (
            <Grid item xs={12} sm={6} md={4} key={c.id}>
              <SavedCard card={c} onDelete={setDeleteTarget} />
            </Grid>
          ))}
        </Grid>
      )}

      {/* Add card dialog */}
      <Dialog
        open={open}
        onClose={() => !submitting && setOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ fontWeight: 700, pb: 0.5 }}>Add a card</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" mb={2}>
            Your card number is encrypted in your browser before it leaves this
            device.
          </Typography>

          {formError && (
            <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
              {formError}
            </Alert>
          )}

          <Stack spacing={1.75}>
            <TextField
              fullWidth
              label="Card Number"
              value={form.number}
              onChange={(e) =>
                setForm((f) => ({ ...f, number: formatCardNumber(e.target.value) }))
              }
              placeholder="1234 5678 9012 3456"
              inputProps={{ inputMode: "numeric" }}
              error={form.number !== "" && !numberOk}
              helperText={
                form.number !== "" && !numberOk ? "Enter the 16-digit card number" : ""
              }
              InputProps={{
                sx: { borderRadius: 2, fontWeight: 600, letterSpacing: 1 },
                endAdornment: network ? (
                  <Chip
                    size="small"
                    label={NETWORK_META[network].label}
                    sx={{
                      fontWeight: 700,
                      fontSize: 10.5,
                      color: NETWORK_META[network].color,
                      bgcolor: alpha(NETWORK_META[network].color, 0.12),
                    }}
                  />
                ) : null,
              }}
            />
            <TextField
              fullWidth
              label="Name on Card"
              value={form.name}
              onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
              error={form.name !== "" && !nameOk}
              helperText={
                form.name !== "" && !nameOk ? "Enter the name printed on the card" : ""
              }
              InputProps={{ sx: { borderRadius: 2 } }}
            />
            <Stack direction="row" spacing={1.5}>
              <TextField
                label="Expiry MM"
                value={form.month}
                onChange={(e) =>
                  setForm((f) => ({
                    ...f,
                    month: e.target.value.replace(/\D/g, "").slice(0, 2),
                  }))
                }
                placeholder="MM"
                inputProps={{ inputMode: "numeric" }}
                error={form.month !== "" && !monthOk}
                InputProps={{ sx: { borderRadius: 2 } }}
                sx={{ flex: 1 }}
              />
              <TextField
                label="Expiry YY"
                value={form.year}
                onChange={(e) =>
                  setForm((f) => ({
                    ...f,
                    year: e.target.value.replace(/\D/g, "").slice(0, 2),
                  }))
                }
                placeholder="YY"
                inputProps={{ inputMode: "numeric" }}
                error={form.year !== "" && !yearOk}
                InputProps={{ sx: { borderRadius: 2 } }}
                sx={{ flex: 1 }}
              />
            </Stack>
          </Stack>

          <Stack
            direction="row"
            spacing={0.75}
            alignItems="center"
            mt={2.5}
            sx={{ color: "text.secondary" }}
          >
            <LockRoundedIcon sx={{ fontSize: 15 }} />
            <Typography variant="caption">
              No CVV is stored — you'll enter it each time you pay.
            </Typography>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, pt: 1 }}>
          <Button
            onClick={() => setOpen(false)}
            disabled={submitting}
            sx={{ textTransform: "none" }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleAdd}
            disabled={submitting || !formOk}
            sx={{ borderRadius: 2.5, px: 3, textTransform: "none", fontWeight: 700 }}
          >
            {submitting ? "Saving…" : "Save Card"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete confirm dialog */}
      <Dialog
        open={!!deleteTarget}
        onClose={() => !deleting && setDeleteTarget(null)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ fontWeight: 700 }}>Delete card?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            This will remove the card ending in ••••{" "}
            {deleteTarget?.last4} from your account. You can add it again later.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, pt: 1 }}>
          <Button
            onClick={() => setDeleteTarget(null)}
            disabled={deleting}
            sx={{ textTransform: "none" }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDelete}
            disabled={deleting}
            sx={{ borderRadius: 2.5, px: 3, textTransform: "none", fontWeight: 700 }}
          >
            {deleting ? "Deleting…" : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
